import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'

// ─────────────────── Types ───────────────────
type JournalEntry = {
  id: number
  number: string
  date: string
  description: string
  source_type: 'INVOICE' | 'PAYMENT' | 'BILL' | 'MANUAL'
  source_id: number | null
  currency_code: string
  total_debit: string
  total_credit: string
  is_balanced: boolean
}

// ─────────────────── Constants ───────────────────
const SOURCE_BADGE: Record<JournalEntry['source_type'], string> = {
  INVOICE: 'bg-blue-100 text-blue-800',
  PAYMENT: 'bg-green-100 text-green-800',
  BILL: 'bg-amber-100 text-amber-800',
  MANUAL: 'bg-gray-100 text-gray-700',
}

const SOURCE_FILTERS: ('ALL' | JournalEntry['source_type'])[] = [
  'ALL', 'INVOICE', 'PAYMENT', 'BILL', 'MANUAL',
]

export default function FinanceJournalEntries() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'journal-entries'],
    queryFn: async () => (await api.get<JournalEntry[]>('/finance/journal-entries/')).data,
  })

  const [filterSource, setFilterSource] = useState<'ALL' | JournalEntry['source_type']>('ALL')

  const entries = data || []
  const filtered = filterSource === 'ALL'
    ? entries
    : entries.filter((e) => e.source_type === filterSource)

  return (
    <AdminLayout>
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground">Journal Entries</h2>
        <p className="text-sm text-muted-foreground">
          Posted general-ledger transactions. Created automatically when invoices, bills, or payments are posted.
        </p>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Source:</span>
        {SOURCE_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterSource(s)}
            className={`px-3 py-1 rounded ${filterSource === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No journal entries. Post an invoice or payment to GL to see one.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Description</Th>
                <Th>Source</Th>
                <Th className="text-right">Debit</Th>
                <Th className="text-right">Credit</Th>
                <Th className="text-center">Balanced</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((je) => (
                <tr
                  key={je.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/finance/journal-entries/${je.id}`)}
                >
                  <Td className="font-mono text-xs">{je.number}</Td>
                  <Td className="text-xs">{je.date}</Td>
                  <Td>{je.description || <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${SOURCE_BADGE[je.source_type]}`}>
                      {je.source_type}
                    </span>
                  </Td>
                  <Td className="text-right font-mono text-xs">
                    {je.currency_code} {Number(je.total_debit).toFixed(2)}
                  </Td>
                  <Td className="text-right font-mono text-xs">
                    {je.currency_code} {Number(je.total_credit).toFixed(2)}
                  </Td>
                  <Td className="text-center">
                    {je.is_balanced
                      ? <Check className="h-4 w-4 text-green-600 inline" />
                      : <X className="h-4 w-4 text-red-600 inline" />}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}
